import { AnimatePresence, motion } from "motion/react";
import { lazy, Suspense, startTransition, useEffect, useEffectEvent, useState } from "react";
import FocusPanel from "@/components/panels/FocusPanel";
import LocationListPanel from "@/components/panels/LocationListPanel";
import MethodPanel from "@/components/panels/MethodPanel";
import PrayerSummaryCard from "@/components/panels/PrayerSummaryCard";
import TimezonePanel from "@/components/panels/TimezonePanel";
import TodayPanel from "@/components/panels/TodayPanel";
import GlobeErrorBoundary from "@/components/ui/GlobeErrorBoundary";
import LearningPathNav from "@/components/ui/LearningPathNav";
import LoadingScreen from "@/components/ui/LoadingScreen";
import WelcomeButton from "@/components/ui/WelcomeButton";
import { appName, hints } from "@/lib/constants";
import { defaultLocationId, getAdjacentLocationId, getLocationById, prayerLocations } from "@/lib/locations";
import { logger } from "@/lib/logger";
import { useOnboardingStore } from "@/store/use-onboarding-store";
import { usePrayerStore } from "@/store/use-prayer-store";
import { useUIStore } from "@/store/use-ui-store";
import type { PrayerLocation, PrayerMethodConfig, PrayerMethodValue, PrayerSnapshot } from "@/types/prayer";

const desktopLeftPanelVariants = {
  exit: { opacity: 0, x: -400 },
  hidden: { opacity: 0, x: -400 },
  visible: { opacity: 1, x: 0 },
};

const mobileLeftPanelVariants = {
  exit: { opacity: 0, y: 40 },
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

const rightPanelVariants = {
  exit: { opacity: 0, x: 100 },
  hidden: { opacity: 0, x: 100 },
  visible: { opacity: 1, x: 0 },
};

const panelTransition = { damping: 25, stiffness: 200, type: "spring" as const };
const PrayerGlobeExperience = lazy(() => import("@/components/globe/PrayerGlobeExperience"));
type GlobeStatus = "failed" | "loading" | "ready";

const useMinTimeElapsed = (delayMs: number) => {
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => setMinTimeElapsed(true), delayMs);
    return () => clearTimeout(timeoutId);
  }, [delayMs]);

  return minTimeElapsed;
};

const useAutoHideTitle = (isLoaded: boolean, hasSeenWelcome: boolean, delayMs: number) => {
  const [showTitle, setShowTitle] = useState(true);

  useEffect(() => {
    if (!isLoaded || !hasSeenWelcome) {
      return;
    }

    const timeoutId = setTimeout(() => setShowTitle(false), delayMs);
    return () => clearTimeout(timeoutId);
  }, [delayMs, hasSeenWelcome, isLoaded]);

  return showTitle;
};

const useResponsiveIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  const syncMobileState = useEffectEvent(() => {
    setIsMobile(window.innerWidth < 768);
  });

  useEffect(() => {
    syncMobileState();
    window.addEventListener("resize", syncMobileState);

    return () => window.removeEventListener("resize", syncMobileState);
  }, []);

  return isMobile;
};

const useInitializePrayerStore = (initializePrayerStore: () => () => void) => {
  useEffect(() => {
    logger.info("App initialized", {
      defaultLocationId,
      locationCount: prayerLocations.length,
    });

    return initializePrayerStore();
  }, [initializePrayerStore]);
};

const useEnsureDefaultSelection = (
  activeStep: number,
  selectedLocationId: string | null,
  selectLocation: (locationId: string) => void,
) => {
  useEffect(() => {
    if (activeStep > 0 && !selectedLocationId) {
      selectLocation(defaultLocationId);
    }
  }, [activeStep, selectedLocationId, selectLocation]);
};

const useGlobeHandlers = (
  globeRetryKey: number,
  setGlobeRetryKey: (value: number) => void,
  setGlobeStatus: (value: GlobeStatus) => void,
) => {
  const handleGlobeReady = useEffectEvent(() => {
    logger.info("Globe experience ready", {
      retryKey: globeRetryKey,
    });
    setGlobeStatus("ready");
  });

  const handleGlobeRuntimeError = useEffectEvent(() => {
    logger.error("Globe experience moved into fallback mode.", {
      retryKey: globeRetryKey,
    });
    setGlobeStatus("failed");
  });

  const handleRetryGlobe = () => {
    const nextRetryKey = globeRetryKey + 1;

    logger.warn("Retrying globe experience.", {
      retryKey: nextRetryKey,
    });

    setGlobeStatus("loading");
    setGlobeRetryKey(nextRetryKey);
  };

  return { handleGlobeReady, handleGlobeRuntimeError, handleRetryGlobe };
};

const useNavigationHandlers = ({
  activeStep,
  focusAnchorLocationId,
  focusLocation,
  resetFocus,
  selectLocation,
  selectedLocationId,
  setActiveStep,
  setPrayerMethod,
}: {
  activeStep: number;
  focusAnchorLocationId: string | null;
  focusLocation: (locationId: string) => void;
  resetFocus: () => void;
  selectLocation: (locationId: string) => void;
  selectedLocationId: string | null;
  setActiveStep: (step: number) => void;
  setPrayerMethod: (method: PrayerMethodValue) => void;
}) => {
  const ensureLocationBeforeNavigating = (step: number) => {
    startTransition(() => {
      if (step > 0 && !selectedLocationId) {
        logger.info("Navigation requested with no selection, defaulting location.", {
          fallbackLocationId: defaultLocationId,
          step,
        });
        selectLocation(defaultLocationId);
      }

      logger.info("Navigation step changed", {
        step,
      });
      setActiveStep(step);
    });
  };

  const focusLocationById = (locationId: string, source: string) => {
    const nextLocationSelection = getLocationById(locationId);

    logger.info("Location focus requested", {
      city: nextLocationSelection?.city,
      locationId,
      source,
    });

    startTransition(() => {
      focusLocation(locationId);

      if (activeStep === 0) {
        setActiveStep(1);
      }
    });
  };

  const handleLocationSelect = (locationId: string) => {
    focusLocationById(locationId, activeStep === 0 ? "globe-marker" : "location-list");
  };

  const handleMethodSelect = (method: PrayerMethodValue) => {
    logger.info("Prayer method selection requested", {
      method,
    });

    startTransition(() => {
      setPrayerMethod(method);
    });
  };

  const handleFocusSelected = () => {
    if (selectedLocationId) {
      focusLocationById(selectedLocationId, "focus-panel-selected");
    }
  };

  const handleFocusPrevious = () => {
    focusLocationById(getAdjacentLocationId(focusAnchorLocationId, -1), "focus-panel-previous");
  };

  const handleFocusNext = () => {
    focusLocationById(getAdjacentLocationId(focusAnchorLocationId, 1), "focus-panel-next");
  };

  const handleResetFocus = (source: string) => {
    logger.info("Focus reset", {
      selectedLocationId,
      source,
    });
    resetFocus();
  };

  return {
    ensureLocationBeforeNavigating,
    handleFocusNext,
    handleFocusPrevious,
    handleFocusSelected,
    handleLocationSelect,
    handleMethodSelect,
    handleResetFocus,
  };
};

const useLocationState = (
  focusedLocationId: string | null,
  selectedLocationId: string | null,
  snapshots: Record<string, PrayerSnapshot>,
) => {
  const selectedLocation = selectedLocationId ? (getLocationById(selectedLocationId) ?? null) : null;
  const focusedLocation = focusedLocationId ? (getLocationById(focusedLocationId) ?? null) : null;
  const selectedSnapshot = selectedLocation ? (snapshots[selectedLocation.id] ?? null) : null;
  const focusAnchorLocationId = focusedLocationId ?? selectedLocationId;
  const previousLocation = getLocationById(getAdjacentLocationId(focusAnchorLocationId, -1)) ?? null;
  const nextLocation = getLocationById(getAdjacentLocationId(focusAnchorLocationId, 1)) ?? null;

  return {
    focusAnchorLocationId,
    focusedLocation,
    nextLocation,
    previousLocation,
    selectedLocation,
    selectedSnapshot,
  };
};

type LeftPanelContentProps = {
  activeStep: number;
  location: PrayerLocation | null;
  methodConfig: PrayerMethodConfig;
  onSelectLocation: (locationId: string) => void;
  onSelectMethod: (method: PrayerMethodValue) => void;
  selectedLocationId: string | null;
  snapshot: PrayerSnapshot | null;
  snapshots: Record<string, PrayerSnapshot>;
};

const renderLeftPanelContent = ({
  activeStep,
  location,
  methodConfig,
  onSelectLocation,
  onSelectMethod,
  selectedLocationId,
  snapshot,
  snapshots,
}: LeftPanelContentProps) => {
  if (activeStep === 1) {
    return (
      <LocationListPanel onSelect={onSelectLocation} selectedLocationId={selectedLocationId} snapshots={snapshots} />
    );
  }

  if (activeStep === 2) {
    return <TodayPanel location={location} snapshot={snapshot} />;
  }

  if (activeStep === 3) {
    return (
      <MethodPanel
        location={location}
        methodConfig={methodConfig}
        onSelectMethod={onSelectMethod}
        snapshot={snapshot}
      />
    );
  }

  if (activeStep === 4) {
    return <TimezonePanel location={location} snapshot={snapshot} />;
  }

  return null;
};

const App = () => {
  const [globeRetryKey, setGlobeRetryKey] = useState(0);
  const [globeStatus, setGlobeStatus] = useState<GlobeStatus>("loading");

  const initializePrayerStore = usePrayerStore((state) => state.initialize);
  const methodConfig = usePrayerStore((state) => state.methodConfig);
  const prayerReady = usePrayerStore((state) => state.isReady);
  const setPrayerMethod = usePrayerStore((state) => state.setMethod);
  const snapshots = usePrayerStore((state) => state.snapshots);

  const activeStep = useUIStore((state) => state.activeStep);
  const focusLocation = useUIStore((state) => state.focusLocation);
  const focusedLocationId = useUIStore((state) => state.focusedLocationId);
  const isZoomed = useUIStore((state) => state.isZoomed);
  const resetFocus = useUIStore((state) => state.resetFocus);
  const selectLocation = useUIStore((state) => state.selectLocation);
  const selectedLocationId = useUIStore((state) => state.selectedLocationId);
  const setActiveStep = useUIStore((state) => state.setActiveStep);

  const hasSeenWelcome = useOnboardingStore((state) => state.hasSeenWelcome);

  const { focusAnchorLocationId, focusedLocation, nextLocation, previousLocation, selectedLocation, selectedSnapshot } =
    useLocationState(focusedLocationId, selectedLocationId, snapshots);
  const isLanding = activeStep === 0;
  const isMobile = useResponsiveIsMobile();
  const minTimeElapsed = useMinTimeElapsed(800);
  const isLoaded = globeStatus !== "loading" && minTimeElapsed && prayerReady;
  const showTitle = useAutoHideTitle(isLoaded, hasSeenWelcome, 5000);

  useInitializePrayerStore(initializePrayerStore);
  useEnsureDefaultSelection(activeStep, selectedLocationId, selectLocation);

  const { handleGlobeReady, handleGlobeRuntimeError, handleRetryGlobe } = useGlobeHandlers(
    globeRetryKey,
    setGlobeRetryKey,
    setGlobeStatus,
  );

  const {
    ensureLocationBeforeNavigating,
    handleFocusNext,
    handleFocusPrevious,
    handleFocusSelected,
    handleLocationSelect,
    handleMethodSelect,
    handleResetFocus,
  } = useNavigationHandlers({
    activeStep,
    focusAnchorLocationId,
    focusLocation,
    resetFocus,
    selectLocation,
    selectedLocationId,
    setActiveStep,
    setPrayerMethod,
  });

  const leftPanelVariants = isMobile ? mobileLeftPanelVariants : desktopLeftPanelVariants;
  const rightPanelContent = (
    <PrayerSummaryCard
      isFocused={isZoomed}
      location={selectedLocation}
      methodConfig={methodConfig}
      onResetFocus={() => handleResetFocus("summary-card")}
      snapshot={selectedSnapshot}
    />
  );

  const leftPanelContent =
    renderLeftPanelContent({
      activeStep,
      location: selectedLocation,
      methodConfig,
      onSelectLocation: handleLocationSelect,
      onSelectMethod: handleMethodSelect,
      selectedLocationId,
      snapshot: selectedSnapshot,
      snapshots,
    }) ??
    (activeStep === 5 ? (
      <FocusPanel
        focusedLocation={focusedLocation}
        isFocused={isZoomed}
        nextLocation={nextLocation}
        onFocusNext={handleFocusNext}
        onFocusPrevious={handleFocusPrevious}
        onFocusSelected={handleFocusSelected}
        onResetFocus={() => handleResetFocus("focus-panel")}
        previousLocation={previousLocation}
        selectedLocation={selectedLocation}
        snapshot={selectedSnapshot}
      />
    ) : null);

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-[#0a0a0a]">
      <AnimatePresence>{!isLoaded && <LoadingScreen />}</AnimatePresence>

      <div className="fixed right-3 top-3 z-30 flex items-center gap-2 md:right-5 md:top-5">
        <a
          aria-label="GitHub repository"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-zinc-800/60 bg-zinc-950/70 text-zinc-400 transition-colors hover:border-emerald-500/40 hover:text-emerald-300"
          href="https://github.com/ragaeeb/one-ummah"
          rel="noreferrer"
          target="_blank"
        >
          <span className="sr-only">GitHub</span>
          <svg aria-hidden="true" className="h-4 w-4" fill="currentColor" role="img" viewBox="0 0 24 24">
            <path d="M12 .5c-6.63 0-12 5.47-12 12.22 0 5.4 3.44 9.98 8.2 11.6.6.12.82-.27.82-.6 0-.3-.01-1.1-.02-2.16-3.34.75-4.04-1.66-4.04-1.66-.55-1.42-1.34-1.8-1.34-1.8-1.1-.77.08-.76.08-.76 1.22.09 1.86 1.28 1.86 1.28 1.08 1.9 2.83 1.35 3.52 1.03.11-.81.42-1.35.76-1.66-2.66-.31-5.46-1.37-5.46-6.08 0-1.34.46-2.43 1.24-3.29-.12-.31-.54-1.56.12-3.26 0 0 1.01-.33 3.3 1.26a11.2 11.2 0 0 1 6 0c2.29-1.59 3.3-1.26 3.3-1.26.66 1.7.24 2.95.12 3.26.77.86 1.24 1.95 1.24 3.29 0 4.72-2.81 5.76-5.49 6.07.43.38.82 1.12.82 2.26 0 1.63-.02 2.94-.02 3.34 0 .33.22.72.83.6 4.76-1.62 8.2-6.2 8.2-11.6C24 5.97 18.63.5 12 .5z" />
          </svg>
        </a>
        <WelcomeButton />
      </div>

      <div
        className={`absolute inset-0 transition-transform duration-700 ease-in-out ${
          isLanding ? "translate-x-0" : "-translate-y-[20vh] md:translate-y-0 md:translate-x-[190px]"
        }`}
      >
        <GlobeErrorBoundary onError={handleGlobeRuntimeError} onRetry={handleRetryGlobe} retryKey={globeRetryKey}>
          <Suspense fallback={null}>
            <PrayerGlobeExperience
              activeLocation={selectedLocation}
              focusedLocation={focusedLocation}
              key={globeRetryKey}
              locations={prayerLocations}
              onBackgroundClick={() => {
                if (isZoomed) {
                  handleResetFocus("globe-background");
                }
              }}
              onLocationSelect={handleLocationSelect}
              onReady={handleGlobeReady}
              selectedLocationId={selectedLocationId}
              snapshots={snapshots}
            />
          </Suspense>
        </GlobeErrorBoundary>
      </div>

      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-40 bg-linear-to-b from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent md:h-72" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-40 bg-linear-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent md:h-72" />

      <AnimatePresence>
        {isLanding && (
          <motion.div
            animate={{ opacity: 1 }}
            className="pointer-events-none absolute inset-x-0 top-0 z-20 flex flex-col items-center px-4 pt-16 md:pt-10"
            exit={{ opacity: 0, y: -30 }}
            initial={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          >
            <p className="text-sm font-medium uppercase tracking-widest text-emerald-400">Interactive Guide</p>
            <AnimatePresence>
              {showTitle && (
                <motion.h1
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-3 text-center text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl"
                  exit={{ opacity: 0, y: -20 }}
                  initial={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                >
                  <span className="text-zinc-50">Ummah </span>
                  <span className="bg-linear-to-r from-emerald-400 to-emerald-200 bg-clip-text text-transparent">
                    Wahidah
                  </span>
                </motion.h1>
              )}
            </AnimatePresence>
            <p className="mt-4 max-w-xl text-center text-sm text-zinc-400">
              See the live prayer state for curated world cities, then focus one coordinate to inspect today's timings.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {!isLanding && (
          <motion.div
            animate="visible"
            className="fixed top-[50vh] right-0 bottom-0 left-0 z-20 flex flex-col rounded-t-2xl border-t border-zinc-800/50 bg-zinc-950/95 backdrop-blur-md md:absolute md:top-0 md:right-auto md:bottom-0 md:left-0 md:w-[380px] md:rounded-none md:border-0 md:bg-transparent md:p-4 md:backdrop-blur-none"
            exit="exit"
            initial="hidden"
            key={`left-${activeStep}`}
            transition={panelTransition}
            variants={leftPanelVariants}
          >
            <div className="min-h-0 flex-1 overflow-y-auto p-4 md:h-full md:p-0">{leftPanelContent}</div>

            <div className="flex shrink-0 items-center justify-between gap-1.5 border-t border-zinc-800/50 px-2 py-2 pb-safe md:hidden">
              <button
                aria-label="Previous step"
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-zinc-800 bg-zinc-900/80 text-zinc-400 transition-colors hover:border-emerald-500/50 hover:text-emerald-400 ${
                  activeStep <= 0 ? "pointer-events-none opacity-30" : ""
                }`}
                onClick={() => ensureLocationBeforeNavigating(Math.max(activeStep - 1, 0))}
                type="button"
              >
                <svg aria-hidden="true" className="shrink-0" fill="none" height="12" viewBox="0 0 16 16" width="12">
                  <path
                    d="M10 3l-5 5 5 5"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                  />
                </svg>
              </button>

              <LearningPathNav activeStep={activeStep} compact onStepChange={ensureLocationBeforeNavigating} />

              <button
                aria-label={activeStep >= 5 ? "Start over" : "Next step"}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-zinc-800 bg-zinc-900/80 text-zinc-400 transition-colors hover:border-emerald-500/50 hover:text-emerald-400"
                onClick={() => ensureLocationBeforeNavigating(activeStep >= 5 ? 0 : Math.min(activeStep + 1, 5))}
                type="button"
              >
                <svg aria-hidden="true" className="shrink-0" fill="none" height="12" viewBox="0 0 16 16" width="12">
                  <path
                    d="M6 3l5 5-5 5"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                  />
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {!isLanding && (
          <motion.div
            animate="visible"
            className="hidden md:block absolute top-14 right-4 z-20 w-[320px]"
            data-testid="desktop-right-panel"
            exit="exit"
            initial="hidden"
            key={`right-${activeStep}`}
            transition={panelTransition}
            variants={rightPanelVariants}
          >
            {rightPanelContent}
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className={`hidden md:flex absolute right-0 z-20 flex-col items-center gap-3 pb-6 transition-all duration-500 ease-in-out left-0 ${
          isLanding ? "bottom-0" : "bottom-0 md:left-[380px]"
        }`}
      >
        <LearningPathNav activeStep={activeStep} onStepChange={ensureLocationBeforeNavigating} />
        <p className="text-xs text-zinc-600">{hints[activeStep]}</p>
      </div>

      {isLanding && (
        <div className="absolute bottom-0 left-0 right-0 z-20 flex flex-col items-center gap-2 pb-4 md:hidden">
          <LearningPathNav activeStep={activeStep} onStepChange={ensureLocationBeforeNavigating} />
          <p className="text-xs text-zinc-600">{hints[activeStep]}</p>
        </div>
      )}

      {!isLanding && isMobile && (
        <div className="absolute top-4 left-4 right-4 z-20 md:hidden" data-testid="mobile-summary-panel">
          {rightPanelContent}
        </div>
      )}

      <div className="pointer-events-none absolute left-4 bottom-4 z-20 hidden md:block">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-700">{appName}</p>
      </div>
    </div>
  );
};

export default App;
